import { type CollectionView, EventListener , View } from "silver";
import { html } from "core/Controls/Books/TeacherBookControl.html";
import { type Book as BookData, BookLength, getBookLength } from "core/Data/Book";
import { LanguageCodes } from "core/Misc";
import {
	AbstractBookControl,
	type BookControlMetric,
	type BookControlView,
	type BookControlViewData
} from "core/Controls/Books/BookControl";

interface TeacherBookControlViewData extends BookControlViewData {
	title: string;
	description: string;
	cardMetrics: Array<{ metric: BookControlMetric }>;
	showAssignButton: boolean;
	lengthClass: string;
}

interface TeacherBookControlView extends BookControlView<TeacherBookControlViewData> {
	coverCell: HTMLDivElement;
	assignButton: HTMLButtonElement;
	myListButton: HTMLButtonElement;
	extraActions: HTMLElement;
	cardMetrics: CollectionView<{ metric: BookControlMetric<any> }>;
}

export class TeacherBookControl extends AbstractBookControl {

	public readonly onRequestInfo = EventListener.create<{ sender: TeacherBookControl }>("requestInfo");
	public readonly onRequestRead = EventListener.create<{ sender: TeacherBookControl }>("requestRead");
	public readonly onRequestAssign = EventListener.create<{ sender: TeacherBookControl }>("requestAssign");

	protected view: TeacherBookControlView;
	private extraActions: HTMLElement[] = [];

	constructor(
		public readonly id: number,
		showAssignButton: boolean
	) {
		super(id);
		this.view = new View<TeacherBookControlViewData>(html, {
			getCoverAlt: (title: string): string => {
				if (! this.bookLang) {
					return "";
				}
				return this.bookLang.ALT_BOOK_COVER_IMG(title);
			},
			handleCheckChange: (): void => {
				this.onCheckChange.trigger({ sender: this });
			},
			formatMetricValue: <T>(metric: BookControlMetric<T>): string|Node|null => {
				if (this.bookData === undefined) {
					return "—";
				}
				const value = metric.value(this.bookData);
				if (value === null) {
					return "—";
				}
				return metric.valueFormatted(value as Exclude<T, null>, this.bookData);
			},
			handleInfoClicked: (event) => {
				event.stopPropagation();
				this.onRequestInfo.trigger({
					sender: this
				});
			},
			handleAssignButtonClicked: (event: MouseEvent) => {
				this.onRequestAssign.trigger({ sender: this });
				event.stopPropagation();
			},
			handleReadButtonClicked: (event: MouseEvent) => {
				this.onRequestRead.trigger({ sender: this });
				event.stopPropagation();
			},
			handleMyListButtonClicked: (event: MouseEvent) => {
				this.setOnMyList(!this.onMyList, this, this.view.myListButton);
				event.stopPropagation();
			}
		}, {
			showAssignButton,
			lang:           { },
			title:          "",
			bookId:         id,
			description:    "",
			isOnMyList:     false,
			readLink:       "",
			atos:           null,
			card:           true,
			row:            false,
			checkbox:       false,
			lengthClass:    "",
			listMetrics:    [],
			listLabels:     [],
			cardMetrics:    [],
			langString:     "",
		}) as TeacherBookControlView;
		this.updateCoverURL();
	}

	public clearExtraActions(): void {
		this.extraActions.forEach(el => {
			if (el.parentElement) {
				el.parentElement.removeChild(el);
			}
		});
	}

	public addExtraAction(label: string|Node, icon: string|HTMLElement, action: string|((e: MouseEvent) => any)): HTMLElement {
		let element: HTMLElement;
		let iconEl: HTMLElement;
		if (typeof action === "string") {
			const a = document.createElement("a");
			a.href = action;
			element = a;
		} else {
			const button = document.createElement("button");
			button.type = "button";
			button.addEventListener("click", action);
			element = button;
		}

		if (typeof icon === "string") {
			iconEl = document.createElement("i");
			iconEl.className = icon;
		} else {
			iconEl = icon;
		}

		iconEl.classList.add("link_icon");
		element.classList.add("link", "book_extraAction");
		element.append(iconEl, label);
		this.view.extraActions.append(element);
		this.extraActions.push(element);
		return element;
	}

	public setCardMetrics(cardMetrics: Array<BookControlMetric<any>>): void {
		this.view.set({ cardMetrics: cardMetrics.map(metric => ({ metric })) });
	}

	get data(): BookData|undefined {
		return this.bookData;
	}

	/**
	 * We have to allow undefined data to match the `get data()` signature, but we won't accept it
	 */
	set data(data: BookData|undefined) {
		if (data === undefined) {
			return;
		}
		const length = getBookLength(data);
		const langIds = [...data.languageIds].sort((a, b) => b - a);
		const langId = langIds[0] || 1;

		this.bookData = data;
		this.view.set({
			title:            data.title,
			description:      data.description,
			arQuizLevel:      data.arQuizLevel ? String(data.archiveCode) : undefined,
			lexile:           data.lexileLabel,
			audioRuntimeSecs: data.audioRuntimeSecs,
			lengthClass:      length === BookLength.UNKNOWN ? "" : ("-" + length),
			atos:             data.arQuizLevel,
			langString:       LanguageCodes[langId]
		});
		this.view.listMetrics.invalidate("metric");
		this.view.cardMetrics.invalidate("metric");
		this.view.listLabels.invalidate("metric");
		this.updateCoverURL();
	}

}
